import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';

const STATUS_OPTIONS = ['draft', 'submitted', 'review', 'offer', 'accepted', 'rejected'];

const UniversityApplicationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [app, setApp] = useState(null);
  const [docs, setDocs] = useState([]);
  const [caseRec, setCaseRec] = useState(null);
  const [clientRec, setClientRec] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [taskScope, setTaskScope] = useState('case'); // case | app
  const [taskFilter, setTaskFilter] = useState('all');
  const [newTask, setNewTask] = useState({ title: '', due_date: '' });
  const [loading, setLoading] = useState(true);
  const [controlStatus, setControlStatus] = useState(null);
  const [toast, setToast] = useState(null);
  const [savingStatus, setSavingStatus] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    portal_ref: '',
    application_fee: '',
    deadline: '',
    submitted_at: '',
    status: ''
  });
  const [deleting, setDeleting] = useState(false);
  const [taskSaving, setTaskSaving] = useState(false);
  const [docSaving, setDocSaving] = useState(false);
  const [newDoc, setNewDoc] = useState({ notes: '', status: 'pending' });

  const showToast = (type, message) => {
    setToast({ type, message });
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast(null), 2500);
  };

  const load = async () => {
    try {
      setLoading(true);
      const { data: appRow, error: appErr } = await supabase
        .from('uni_applications')
        .select('*, programs(name, universities(name)), intakes(term), clients(first_name,last_name,primary_email), cases(title)')
        .eq('id', id)
        .single();
      if (appErr) throw appErr;
      setApp(appRow || null);
      setEditForm({
        portal_ref: appRow?.metadata?.portal_ref || '',
        application_fee: appRow?.metadata?.application_fee || '',
        deadline: appRow?.metadata?.deadline || '',
        submitted_at: appRow?.submitted_at ? appRow.submitted_at.split('T')[0] : '',
        status: appRow?.status || 'draft'
      });

      if (appRow?.case_id) {
        const { data: c } = await supabase.from('cases').select('id,title,status,client_id').eq('id', appRow.case_id).single();
        setCaseRec(c || null);
        const { data: t } = await supabase.from('case_tasks').select('*').eq('case_id', appRow.case_id).order('created_at', { ascending: false });
        setTasks(t || []);
        if (c?.client_id) {
          const { data: cl } = await supabase.from('clients').select('id, first_name, last_name, primary_email').eq('id', c.client_id).single();
          setClientRec(cl || null);
        }
      } else {
        setCaseRec(null);
        setTasks([]);
        setClientRec(appRow?.clients || null);
      }

      const { data: docRows } = await supabase
        .from('uni_app_documents')
        .select('*')
        .eq('uni_application_id', id)
        .order('created_at', { ascending: false });
      setDocs(docRows || []);

      // Control Room status (deadline awareness)
      try {
        const { data: deadlineRow } = await supabase
          .from('unified_deadlines')
          .select('deadline, deadline_type')
          .eq('entity_id', id)
          .eq('deadline_type', 'university_application')
          .single();
        if (deadlineRow?.deadline) {
          const deadlineDate = new Date(deadlineRow.deadline);
          const now = new Date();
          const diffDays = Math.floor((deadlineDate - now) / (1000 * 60 * 60 * 24));
          let level = 'attention';
          if (deadlineDate < now || diffDays <= 0) level = 'critical';
          else if (diffDays <= 3) level = 'urgent';
          setControlStatus({
            level,
            deadline: deadlineDate,
            days: diffDays
          });
        } else {
          setControlStatus(null);
        }
      } catch (deadlineErr) {
        console.warn('Uni app control status lookup failed', deadlineErr);
        setControlStatus(null);
      }
    } catch (err) {
      console.error('Failed to load university application', err);
      showToast('error', 'Failed to load application');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(t);
  }, [toast]);

  const checklistProgress = useMemo(() => {
    const total = docs.length;
    const done = docs.filter(d => d.status === 'approved' || d.status === 'uploaded' || d.status === 'submitted').length;
    return { done, total };
  }, [docs]);

  const filteredTasks = useMemo(() => {
    if (!tasks || !tasks.length) return [];
    let list = tasks;
    if (taskScope === 'app') {
      const needles = [
        app?.id?.toLowerCase?.(),
        app?.programs?.name?.toLowerCase?.(),
        app?.metadata?.program_text?.toLowerCase?.(),
        app?.programs?.universities?.name?.toLowerCase?.(),
        app?.metadata?.university?.toLowerCase?.()
      ].filter(Boolean);
      if (needles.length) {
        list = list.filter(t => {
          const hay = `${t.title || ''} ${t.description || ''}`.toLowerCase();
          return needles.some(n => hay.includes(n));
        });
      } else {
        list = [];
      }
    }
    if (taskFilter === 'due') list = list.filter(t => t.due_date);
    return list;
  }, [tasks, taskFilter, taskScope, app]);

  const updateStatus = async (newStatus) => {
    if (!app) return;
    try {
      setSavingStatus(true);
      const { error } = await supabase.from('uni_applications').update({ status: newStatus }).eq('id', app.id);
      if (error) throw error;
      showToast('success', 'Status updated');
      await load();
    } catch (err) {
      console.error('Failed to update status', err);
      showToast('error', 'Failed to update status');
    } finally {
      setSavingStatus(false);
    }
  };

  const saveEdits = async () => {
    if (!app) return;
    try {
      setSavingStatus(true);
      const newMeta = {
        ...app.metadata,
        portal_ref: editForm.portal_ref || null,
        application_fee: editForm.application_fee || null,
        deadline: editForm.deadline || null
      };
      const payload = {
        status: editForm.status || app.status,
        submitted_at: editForm.submitted_at || null,
        metadata: newMeta
      };
      const { error } = await supabase.from('uni_applications').update(payload).eq('id', app.id);
      if (error) throw error;
      showToast('success', 'Application updated');
      setEditMode(false);
      await load();
    } catch (err) {
      console.error('Failed to update application', err);
      showToast('error', 'Failed to update application');
    } finally {
      setSavingStatus(false);
    }
  };

  const deleteApp = async () => {
    if (!app) return;
    const ok = window.confirm('Delete this university application? This cannot be undone.');
    if (!ok) return;
    try {
      setDeleting(true);
      const { error } = await supabase.from('uni_applications').delete().eq('id', app.id);
      if (error) throw error;
      showToast('success', 'Application deleted');
      navigate('/admin/uni-apps');
    } catch (err) {
      console.error('Failed to delete application', err);
      showToast('error', 'Failed to delete application');
    } finally {
      setDeleting(false);
    }
  };

  const addTask = async () => {
    if (!app?.case_id || !newTask.title.trim()) return;
    try {
      setTaskSaving(true);
      const { error } = await supabase.from('case_tasks').insert([{
        case_id: app.case_id,
        title: `${newTask.title.trim()} — (App ${app.id})`,
        due_date: newTask.due_date || null,
        status: 'todo'
      }]);
      if (error) throw error;
      setNewTask({ title: '', due_date: '' });
      await load();
      showToast('success', 'Task added to case');
    } catch (err) {
      console.error('Failed to add task', err);
      showToast('error', 'Failed to add task');
    } finally {
      setTaskSaving(false);
    }
  };

  const updateTaskStatus = async (taskId, status) => {
    try {
      const payload = { status };
      if (status === 'done') payload.completed_at = new Date().toISOString();
      const { error } = await supabase.from('case_tasks').update(payload).eq('id', taskId);
      if (error) throw error;
      await load();
    } catch (err) {
      console.error('Failed to update task', err);
      showToast('error', 'Failed to update task');
    }
  };

  const deleteTask = async (taskId) => {
    const ok = window.confirm('Delete this task from the case?');
    if (!ok) return;
    try {
      const { error } = await supabase.from('case_tasks').delete().eq('id', taskId);
      if (error) throw error;
      await load();
    } catch (err) {
      console.error('Failed to delete task', err);
      showToast('error', 'Failed to delete task');
    }
  };

  const uploadChecklistFile = async (docId, file) => {
    if (!file) return;
    const MAX_SIZE = 10 * 1024 * 1024; // 10MB
    if (file.size > MAX_SIZE) {
      alert(`File is too large. Max size is 10MB.`);
      return;
    }

    try {
      setDocSaving(true);
      const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const path = `uni-apps/${app.id}/${docId}/${Date.now()}_${safeName}`;

      const { error: upErr } = await supabase.storage.from('documents').upload(path, file, { upsert: true });
      if (upErr) throw upErr;

      const { data: pub } = await supabase.storage.from('documents').getPublicUrl(path);

      const { error: dbErr } = await supabase.from('uni_app_documents').update({
        file_url: pub.publicUrl,
        file_path: path,
        status: 'uploaded',
        updated_at: new Date().toISOString()
      }).eq('id', docId);

      if (dbErr) throw dbErr;

      showToast('success', 'File uploaded');
      await load();
    } catch (err) {
      console.error('Failed to upload file', err);
      showToast('error', 'Failed to upload file');
    } finally {
      setDocSaving(false);
    }
  };

  const addChecklistItem = async () => {
    if (!app || !newDoc.notes.trim()) return;
    try {
      setDocSaving(true);
      const payload = {
        uni_application_id: app.id,
        template_id: null,
        status: newDoc.status || 'pending',
        notes: newDoc.notes.trim()
      };
      const { error } = await supabase.from('uni_app_documents').insert([payload]);
      if (error) throw error;
      setNewDoc({ notes: '', status: 'pending' });
      await load();
      showToast('success', 'Checklist item added');
    } catch (err) {
      console.error('Failed to add checklist item', err);
      showToast('error', 'Failed to add checklist item');
    } finally {
      setDocSaving(false);
    }
  };

  const updateChecklistItem = async (docId, updates) => {
    try {
      const { error } = await supabase.from('uni_app_documents').update(updates).eq('id', docId);
      if (error) throw error;
      await load();
    } catch (err) {
      console.error('Failed to update checklist item', err);
      showToast('error', 'Failed to update checklist item');
    }
  };

  const deleteChecklistItem = async (docId) => {
    const ok = window.confirm('Delete this checklist item?');
    if (!ok) return;
    try {
      const { error } = await supabase.from('uni_app_documents').delete().eq('id', docId);
      if (error) throw error;
      await load();
    } catch (err) {
      console.error('Failed to delete checklist item', err);
      showToast('error', 'Failed to delete checklist item');
    }
  };

  const importChecklistFromTemplate = async () => {
    if (!app?.program_id) {
      showToast('error', 'No program selected to import template');
      return;
    }
    try {
      setDocSaving(true);
      const { data: reqs, error } = await supabase.from('program_requirements').select('requirement_key,label,mandatory').eq('program_id', app.program_id);
      if (error) throw error;
      const docsToInsert = (reqs || []).map(r => ({
        uni_application_id: app.id,
        template_id: r.requirement_key || null,
        status: r.mandatory ? 'requested' : 'optional',
        notes: r.label || r.requirement_key || 'Requirement'
      }));
      if (docsToInsert.length) {
        await supabase.from('uni_app_documents').insert(docsToInsert);
        showToast('success', 'Imported checklist from template');
        await load();
      } else {
        showToast('error', 'No template items found');
      }
    } catch (err) {
      console.error('Failed to import checklist', err);
      showToast('error', 'Failed to import checklist');
    } finally {
      setDocSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white font-mono flex flex-col items-center justify-center">
        <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full mb-3"></div>
        <div className="text-gray-400">Loading application...</div>
      </div>
    );
  }

  if (!app) {
    return (
      <div className="min-h-screen bg-gray-900 text-white font-mono flex flex-col items-center justify-center">
        <div className="text-gray-300 text-lg mb-2">Application not found</div>
        <button
          className="px-4 py-2 rounded bg-gray-800 border border-gray-700 text-sm"
          onClick={() => navigate(-1)}
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white font-mono p-6 space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded border ${toast.type === 'error' ? 'bg-red-900/80 border-red-700 text-red-100' : 'bg-green-900/80 border-green-700 text-green-100'}`}>
          {toast.message}
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm text-gray-400 uppercase tracking-widest mb-1">University Application</div>
          <h1 className="text-2xl font-bold text-white">
            {app.programs?.universities?.name || app.metadata?.university || 'University'} • {app.programs?.name || app.metadata?.program_text || 'Program'}
          </h1>
          <div className="text-gray-500 text-sm mt-1 flex items-center gap-3">
            <span>ID: {app.id}</span>
            {app.case_id && (
              <button
                onClick={() => navigate(`/admin/cases/${app.case_id}`)}
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Open Case
              </button>
            )}
          </div>
          {controlStatus && (
            <div className="text-xs text-gray-400 mt-2 flex items-center gap-2">
              <span className={`px-2 py-0.5 rounded border ${controlStatus.level === 'critical' ? 'bg-red-900/30 text-red-300 border-red-800' :
                controlStatus.level === 'urgent' ? 'bg-yellow-900/30 text-yellow-300 border-yellow-800' :
                  'bg-blue-900/30 text-blue-300 border-blue-800'
                }`}>
                {controlStatus.level.toUpperCase()}
              </span>
              <span>
                {controlStatus.deadline ? controlStatus.deadline.toLocaleDateString() : 'No deadline'}
                {typeof controlStatus.days === 'number' ? ` (${controlStatus.days}d)` : ''}
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-3">
          <select
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-xs uppercase"
            value={app.status || 'draft'}
            disabled={savingStatus}
            onChange={(e) => updateStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button
            onClick={() => navigate(-1)}
            className="px-3 py-2 rounded border border-gray-700 bg-gray-800 text-gray-300 hover:bg-gray-700 text-xs uppercase"
          >
            Back
          </button>
          <button
            onClick={() => setEditMode(prev => !prev)}
            className="px-3 py-2 rounded border border-blue-700 bg-blue-900/40 text-blue-200 hover:bg-blue-900/60 text-xs uppercase"
          >
            {editMode ? 'Cancel Edit' : 'Edit'}
          </button>
          <button
            onClick={deleteApp}
            disabled={deleting}
            className="px-3 py-2 rounded border border-red-700 bg-red-900/40 text-red-200 hover:bg-red-900/60 text-xs uppercase"
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-xs text-gray-500 uppercase mb-2">Details</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-gray-500 text-[11px] uppercase tracking-wider">University</div>
                <div className="text-white">{app.programs?.universities?.name || app.metadata?.university || '-'}</div>
              </div>
              <div>
                <div className="text-gray-500 text-[11px] uppercase tracking-wider">Program</div>
                <div className="text-white">{app.programs?.name || app.metadata?.program_text || '-'}</div>
              </div>
              <div>
                <div className="text-gray-500 text-[11px] uppercase tracking-wider">Intake</div>
                <div className="text-white">{app.intakes?.term || '-'}</div>
              </div>
              <div>
                <div className="text-gray-500 text-[11px] uppercase tracking-wider">Portal Ref</div>
                <div className="text-white">
                  {editMode ? (
                    <input
                      className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white w-full"
                      value={editForm.portal_ref}
                      onChange={(e) => setEditForm(f => ({ ...f, portal_ref: e.target.value }))}
                    />
                  ) : (app.metadata?.portal_ref || '-')}
                </div>
              </div>
              <div>
                <div className="text-gray-500 text-[11px] uppercase tracking-wider">Submitted At</div>
                <div className="text-white">
                  {editMode ? (
                    <input
                      type="date"
                      className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white w-full"
                      value={editForm.submitted_at}
                      onChange={(e) => setEditForm(f => ({ ...f, submitted_at: e.target.value }))}
                    />
                  ) : (app.submitted_at ? new Date(app.submitted_at).toLocaleDateString() : '-')}
                </div>
              </div>
              <div>
                <div className="text-gray-500 text-[11px] uppercase tracking-wider">Updated</div>
                <div className="text-white">{app.updated_at ? new Date(app.updated_at).toLocaleString() : '-'}</div>
              </div>
              <div className="md:col-span-2 flex justify-end">
                {editMode && (
                  <button
                    onClick={saveEdits}
                    disabled={savingStatus}
                    className="px-3 py-2 rounded border border-green-700 bg-green-900/40 text-green-200 hover:bg-green-900/60 text-xs uppercase"
                  >
                    {savingStatus ? 'Saving...' : 'Save Changes'}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-gray-500 uppercase">Checklist</div>
              <div className="flex items-center gap-2">
                <button
                  onClick={importChecklistFromTemplate}
                  disabled={docSaving}
                  className="px-3 py-1 rounded bg-gray-900 text-blue-300 border border-gray-700 text-[11px] uppercase"
                >
                  Import Template
                </button>
                <div className="text-[11px] text-gray-400">{checklistProgress.done}/{checklistProgress.total} done</div>
              </div>
            </div>
            <div className="space-y-2">

              {docs.map(d => (
                <div key={d.id} className="flex flex-col gap-2 bg-gray-900/50 border border-gray-700 rounded px-3 py-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 mr-3">
                      <input
                        className="w-full bg-transparent text-white text-sm border border-transparent hover:border-gray-700 rounded px-2 py-1"
                        value={d.notes || ''}
                        onChange={(e) => updateChecklistItem(d.id, { notes: e.target.value })}
                        placeholder="Document description..."
                      />
                      <div className="text-[11px] text-gray-500 mt-1 pl-2 flex gap-2">
                        {d.template_id && <span>Template: {d.template_id}</span>}
                        {d.updated_at && <span>Updated: {new Date(d.updated_at).toLocaleDateString()}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        className="bg-gray-800 border border-gray-700 text-white text-xs rounded px-2 py-1"
                        value={d.status || 'pending'}
                        onChange={(e) => updateChecklistItem(d.id, { status: e.target.value })}
                      >
                        {['pending', 'requested', 'uploaded', 'submitted', 'approved', 'rejected', 'optional'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button
                        onClick={() => deleteChecklistItem(d.id)}
                        className="text-red-400 text-xs hover:text-red-300 px-1"
                        title="Delete Item"
                      >
                        ✕
                      </button>
                    </div>
                  </div>

                  {/* File Attachment Section */}
                  <div className="flex items-center gap-3 pl-2 text-xs border-t border-gray-800 pt-2">
                    {d.file_url ? (
                      <a
                        href={d.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-blue-400 hover:text-blue-300"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                        View File
                      </a>
                    ) : (
                      <span className="text-gray-600 flex items-center gap-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                        No file
                      </span>
                    )}

                    <div className="relative overflow-hidden">
                      <button className="text-gray-400 hover:text-white flex items-center gap-1 cursor-pointer">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                        {d.file_url ? 'Replace' : 'Upload'}
                      </button>
                      <input
                        type="file"
                        className="absolute inset-0 opacity-0 cursor-pointer"
                        onChange={(e) => uploadChecklistFile(d.id, e.target.files[0])}
                        disabled={docSaving}
                      />
                    </div>
                  </div>
                </div>
              ))}
              {docs.length === 0 && (
                <div className="text-center text-gray-500 text-sm py-6 border border-dashed border-gray-700 rounded">No checklist items</div>
              )}
            </div>
            <div className="mt-3 flex flex-col md:flex-row gap-2 items-start md:items-center">
              <input
                className="flex-1 bg-gray-900 border border-gray-700 rounded px-3 py-2 text-sm text-white"
                placeholder="New checklist item"
                value={newDoc.notes}
                onChange={(e) => setNewDoc(d => ({ ...d, notes: e.target.value }))}
              />
              <select
                className="bg-gray-900 border border-gray-700 text-white text-xs rounded px-2 py-2"
                value={newDoc.status}
                onChange={(e) => setNewDoc(d => ({ ...d, status: e.target.value }))}
              >
                {['pending', 'requested', 'uploaded', 'submitted', 'approved', 'rejected', 'optional'].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <button
                onClick={addChecklistItem}
                disabled={docSaving}
                className="px-3 py-2 rounded bg-blue-700 text-white text-xs uppercase border border-blue-600"
              >
                {docSaving ? 'Saving...' : 'Add'}
              </button>
            </div>
          </div>

          {app.case_id && (
            <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="text-xs text-gray-500 uppercase">Case Tasks (synced)</div>
                  <div className="flex bg-gray-900 border border-gray-700 rounded overflow-hidden text-[11px] uppercase">
                    <button
                      className={`px-2 py-1 ${taskScope === 'case' ? 'bg-blue-800 text-white' : 'text-gray-400'}`}
                      onClick={() => setTaskScope('case')}
                    >
                      All Case
                    </button>
                    <button
                      className={`px-2 py-1 ${taskScope === 'app' ? 'bg-blue-800 text-white' : 'text-gray-400'}`}
                      onClick={() => setTaskScope('app')}
                    >
                      This Application
                    </button>
                  </div>
                </div>
                <div className="flex gap-2 items-center">
                  <select
                    className="bg-gray-900 border border-gray-700 text-white text-xs rounded px-2 py-1"
                    value={taskFilter}
                    onChange={(e) => setTaskFilter(e.target.value)}
                  >
                    <option value="all">All tasks</option>
                    <option value="due">With due date</option>
                  </select>
                  <input
                    className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white"
                    placeholder="New task title"
                    value={newTask.title}
                    onChange={(e) => setNewTask(t => ({ ...t, title: e.target.value }))}
                  />
                  <input
                    type="date"
                    className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white"
                    value={newTask.due_date}
                    onChange={(e) => setNewTask(t => ({ ...t, due_date: e.target.value }))}
                  />
                  <button
                    onClick={addTask}
                    disabled={taskSaving}
                    className="px-3 py-1.5 rounded bg-blue-700 text-white text-xs uppercase border border-blue-600"
                  >
                    {taskSaving ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                {filteredTasks.map(t => (
                  <div key={t.id} className="flex items-center justify-between bg-gray-900/50 border border-gray-700 rounded px-3 py-2">
                    <div>
                      <div className="text-white text-sm">{t.title}</div>
                      <div className="text-[11px] text-gray-500 flex gap-2">
                        <span>Status: {t.status || 'todo'}</span>
                        {t.due_date && <span>Due: {t.due_date}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Link
                        to={`/admin/tasks/${t.id}`}
                        className="text-blue-400 text-xs hover:text-blue-300"
                      >
                        View
                      </Link>
                      <select
                        className="bg-gray-800 border border-gray-700 text-white text-xs rounded px-2 py-1"
                        value={t.status || 'todo'}
                        onChange={(e) => updateTaskStatus(t.id, e.target.value)}
                      >
                        {['todo', 'in_progress', 'done'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button
                        onClick={() => deleteTask(t.id)}
                        className="text-red-400 text-xs hover:text-red-300"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
                {tasks.length === 0 && (
                  <div className="text-center text-gray-500 text-sm py-6 border border-dashed border-gray-700 rounded">No case tasks yet</div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-xs text-gray-500 uppercase mb-2">Metadata</div>
            <div className="text-sm text-gray-300 whitespace-pre-wrap break-words">
              <div className="flex justify-between border-b border-gray-700 py-1">
                <span className="text-gray-500 text-[11px] uppercase">Country</span>
                <span>{app.metadata?.country || '-'}</span>
              </div>
              <div className="flex justify-between border-b border-gray-700 py-1">
                <span className="text-gray-500 text-[11px] uppercase">Fee</span>
                <span>
                  {editMode ? (
                    <input
                      className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white w-24"
                      value={editForm.application_fee}
                      onChange={(e) => setEditForm(f => ({ ...f, application_fee: e.target.value }))}
                    />
                  ) : (app.metadata?.application_fee || '-')}
                </span>
              </div>
              <div className="flex justify-between py-1">
                <span className="text-gray-500 text-[11px] uppercase">Deadline</span>
                <span>
                  {editMode ? (
                    <input
                      type="date"
                      className="bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm text-white w-36"
                      value={editForm.deadline}
                      onChange={(e) => setEditForm(f => ({ ...f, deadline: e.target.value }))}
                    />
                  ) : (app.metadata?.deadline || '-')}
                </span>
              </div>
            </div>
          </div>

          <div className="bg-gray-800 border border-gray-700 rounded-lg p-4">
            <div className="text-xs text-gray-500 uppercase mb-2">Associations</div>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between border-b border-gray-700 pb-2">
                <span className="text-gray-500 text-[11px] uppercase">Case</span>
                {caseRec ? (
                  <Link to={`/admin/cases/${caseRec.id}`} className="text-blue-400 hover:text-blue-300">
                    {caseRec.title || caseRec.id}
                  </Link>
                ) : (
                  <span className="text-gray-500">None</span>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 text-[11px] uppercase">Client</span>
                {clientRec ? (
                  <span className="text-gray-300">{`${clientRec.first_name || ''} ${clientRec.last_name || ''}`.trim() || clientRec.primary_email || clientRec.id}</span>
                ) : (
                  <span className="text-gray-500">Unknown</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UniversityApplicationDetailPage;
